
































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { ChatDeSuporteModule } from '@/store/vuex/suporte/ChatDeSuporteStore'
import {
	conversaAberta,
	PreviaDoChat,
	previaDaMensagem,
	rotuloDaBolha,
	rotuloDeNaoLidas,
	ultimaMensagemDoSuporte,
} from '@/shareds/chat-de-suporte'

type Lado = 'esquerda' | 'direita'
type Ponto = { x: number; y: number }
type Pressao = { id: number; x0: number; y0: number; inicio: Ponto; longa: boolean }

const TAMANHO = 56
const MARGEM = 12
const ESPACO = 10
const CHAVE_DA_POSICAO = 'almode_chat_suporte_bolha_v1'
const PREVIA_MS = 4_500
const MENU_MS = 4_000
const PRESSAO_LONGA_MS = 450
const DISTANCIA_DE_ARRASTO = 6

/**
 * Bolha flutuante do Atendimento Almode, com o comportamento do app Almode (components/support-chat-bubble.tsx):
 * a pessoa lê o passo a passo do suporte, sai para fazer no ERP e volta para a conversa com um clique.
 *
 * Quem decide se ela pode aparecer é o ChatDeSuporte (sessão viva do usuário atual, conversa aberta, painel
 * fechado). Aqui mora o resto: "Ocultar", arrasto com encaixe na lateral, prévia da mensagem nova e acessibilidade.
 */
@Component
export default class BolhaDoChatDeSuporte extends Vue {
	@Prop({ type: Boolean, default: false }) elegivel!: boolean

	lado: Lado = 'esquerda'
	razaoY = 1
	largura = 0
	altura = 0
	arrastando = false
	posicaoDeArrasto: Ponto | null = null
	previa: PreviaDoChat | null = null
	menuAberto = false
	pulsando = false
	popDoSelo = 0
	anuncio = ''
	movimentoReduzido = false
	vistoAte = 0

	pressao?: Pressao
	amostras?: { x: number; y: number; t: number }[]
	temporizadorDaPressao?: number
	temporizadorDaPrevia?: number
	temporizadorDoMenu?: number
	temporizadorDoPulso?: number
	preferenciaDeMovimento?: MediaQueryList

	get naoLidas() {
		return ChatDeSuporteModule.naoLidas
	}

	get estado() {
		return ChatDeSuporteModule.estado
	}

	get digitando() {
		return !!this.estado && !!this.estado.typing && conversaAberta(this.estado)
	}

	get atendente() {
		const nome = this.estado && this.estado.agentName
		return (nome && nome.trim()) || null
	}

	get inicialDoAtendente() {
		return this.atendente ? this.atendente.charAt(0).toUpperCase() : null
	}

	get ultimaDoSuporte() {
		return ultimaMensagemDoSuporte(ChatDeSuporteModule.itens)
	}

	get idDaUltimaDoSuporte() {
		return this.ultimaDoSuporte ? this.ultimaDoSuporte.id : 0
	}

	get visivel() {
		return this.elegivel && ChatDeSuporteModule.ocultaAte === null
	}

	get mostraBalao() {
		return this.visivel && !this.arrastando
	}

	get rotulo() {
		return rotuloDaBolha(this.naoLidas, this.digitando, this.atendente)
	}

	get seloDeNaoLidas() {
		return rotuloDeNaoLidas(this.naoLidas)
	}

	/** Selo e inicial do atendente ficam do lado de dentro da tela, longe da borda. */
	get ladoDeDentro(): Lado {
		return this.lado === 'esquerda' ? 'direita' : 'esquerda'
	}

	/** Área livre: sem cobrir a barra lateral nem a barra do topo do ERP. */
	get limites() {
		const aplicacao = this.$vuetify.application
		const esquerda = aplicacao.left + MARGEM
		const direita = Math.max(esquerda, this.largura - TAMANHO - MARGEM - aplicacao.right)
		const topo = aplicacao.top + MARGEM
		const base = Math.max(topo, this.altura - TAMANHO - MARGEM - aplicacao.bottom - aplicacao.footer)
		return { esquerda, direita, topo, base }
	}

	get posicao(): Ponto {
		if (this.posicaoDeArrasto) return this.posicaoDeArrasto
		const { esquerda, direita, topo, base } = this.limites
		return { x: this.lado === 'esquerda' ? esquerda : direita, y: topo + (base - topo) * this.razaoY }
	}

	get larguraDoBalao() {
		return Math.max(160, Math.min(260, this.largura - TAMANHO - MARGEM * 2 - ESPACO - 16))
	}

	/** Prévia ao lado da bolha, para dentro da tela: com a bolha no alto cresce para baixo, senão para cima. */
	get estiloDaPrevia(): Record<string, string> {
		const estilo = this.estiloDoLado()
		if (this.razaoY < 0.35) estilo.top = `${this.posicao.y}px`
		else estilo.bottom = `${this.altura - this.posicao.y - TAMANHO}px`
		return estilo
	}

	estiloNoMeio(alturaDoBalao: number): Record<string, string> {
		return { ...this.estiloDoLado(), top: `${this.posicao.y + TAMANHO / 2 - alturaDoBalao / 2}px` }
	}

	estiloDoLado(): Record<string, string> {
		const estilo: Record<string, string> = { maxWidth: `${this.larguraDoBalao}px` }
		if (this.lado === 'esquerda') estilo.left = `${this.posicao.x + TAMANHO + ESPACO}px`
		else estilo.right = `${this.largura - this.posicao.x + ESPACO}px`
		return estilo
	}

	created() {
		this.medirJanela()
		this.vistoAte = this.idDaUltimaDoSuporte
		try {
			const salva = JSON.parse(localStorage.getItem(CHAVE_DA_POSICAO) || 'null')
			if (salva && (salva.lado === 'esquerda' || salva.lado === 'direita')) this.lado = salva.lado
			if (salva && typeof salva.y === 'number' && Number.isFinite(salva.y)) this.razaoY = Math.min(1, Math.max(0, salva.y))
		} catch {
			// posição é conforto: sem storage, fica no canto padrão
		}
	}

	mounted() {
		window.addEventListener('resize', this.medirJanela)
		if (window.matchMedia) {
			this.preferenciaDeMovimento = window.matchMedia('(prefers-reduced-motion: reduce)')
			this.movimentoReduzido = this.preferenciaDeMovimento.matches
			this.preferenciaDeMovimento.addEventListener('change', this.aoMudarPreferenciaDeMovimento)
		}
	}

	beforeDestroy() {
		window.removeEventListener('resize', this.medirJanela)
		if (this.preferenciaDeMovimento) {
			this.preferenciaDeMovimento.removeEventListener('change', this.aoMudarPreferenciaDeMovimento)
		}
		window.clearTimeout(this.temporizadorDaPressao)
		window.clearTimeout(this.temporizadorDaPrevia)
		window.clearTimeout(this.temporizadorDoMenu)
		window.clearTimeout(this.temporizadorDoPulso)
	}

	@Watch('idDaUltimaDoSuporte')
	aoChegarMensagemDoSuporte(id: number) {
		// Sem bolha (painel aberto, conversa encerrada): o que chegou já foi visto ou não interessa mais
		if (!this.elegivel || id <= this.vistoAte) {
			this.vistoAte = Math.max(this.vistoAte, id)
			return
		}
		this.vistoAte = id
		if (!this.ultimaDoSuporte) return
		this.menuAberto = false
		this.previa = previaDaMensagem(this.ultimaDoSuporte)
		this.anuncio = `Nova mensagem do suporte. ${this.previa.autor}: ${this.previa.texto}`
		window.clearTimeout(this.temporizadorDaPrevia)
		this.temporizadorDaPrevia = window.setTimeout(() => {
			this.previa = null
		}, PREVIA_MS)
		this.pulsar()
	}

	@Watch('elegivel')
	aoMudarElegibilidade(elegivel: boolean) {
		if (elegivel) return
		this.vistoAte = Math.max(this.vistoAte, this.idDaUltimaDoSuporte)
		this.previa = null
		this.menuAberto = false
	}

	@Watch('naoLidas')
	aoMudarNaoLidas(atual: number, anterior: number) {
		if (atual > anterior && !this.movimentoReduzido) this.popDoSelo += 1
	}

	medirJanela() {
		this.largura = document.documentElement.clientWidth || window.innerWidth
		this.altura = window.innerHeight
	}

	aoMudarPreferenciaDeMovimento(evento: MediaQueryListEvent) {
		this.movimentoReduzido = evento.matches
	}

	pulsar() {
		if (this.movimentoReduzido) return
		this.pulsando = false
		this.$nextTick(() => {
			this.pulsando = true
			window.clearTimeout(this.temporizadorDoPulso)
			this.temporizadorDoPulso = window.setTimeout(() => {
				this.pulsando = false
			}, 2_300)
		})
	}

	abrir() {
		this.menuAberto = false
		this.previa = null
		this.$emit('abrir')
	}

	mostrarMenu(focar: boolean) {
		this.previa = null
		this.menuAberto = true
		window.clearTimeout(this.temporizadorDoMenu)
		this.temporizadorDoMenu = window.setTimeout(() => {
			this.menuAberto = false
		}, MENU_MS)
		if (focar) {
			this.$nextTick(() => {
				const menu = this.$refs.menu as HTMLElement | undefined
				if (menu) menu.focus()
			})
		}
	}

	fecharMenu() {
		this.menuAberto = false
	}

	ocultar() {
		this.menuAberto = false
		this.previa = null
		ChatDeSuporteModule.ocultarAte(this.idDaUltimaDoSuporte)
		this.anuncio = 'Bolha do atendimento oculta até a próxima mensagem do suporte.'
	}

	aoTeclar(evento: KeyboardEvent) {
		if (evento.key === 'Enter' || evento.key === ' ') {
			evento.preventDefault()
			this.abrir()
		} else if (evento.key === 'ContextMenu' || (evento.shiftKey && evento.key === 'F10')) {
			evento.preventDefault()
			this.mostrarMenu(true)
		} else if (evento.key === 'Escape') {
			this.menuAberto = false
			this.previa = null
		}
	}

	aoPressionar(evento: PointerEvent) {
		if (evento.button !== 0) return
		const alvo = evento.currentTarget as HTMLElement
		if (alvo.setPointerCapture) alvo.setPointerCapture(evento.pointerId)
		this.pressao = { id: evento.pointerId, x0: evento.clientX, y0: evento.clientY, inicio: { ...this.posicao }, longa: false }
		this.amostras = [{ x: evento.clientX, y: evento.clientY, t: evento.timeStamp }]
		window.clearTimeout(this.temporizadorDaPressao)
		this.temporizadorDaPressao = window.setTimeout(() => {
			if (this.pressao && !this.arrastando) {
				this.pressao.longa = true
				this.mostrarMenu(false)
			}
		}, PRESSAO_LONGA_MS)
	}

	aoMover(evento: PointerEvent) {
		const pressao = this.pressao
		if (!pressao || evento.pointerId !== pressao.id) return
		const dx = evento.clientX - pressao.x0
		const dy = evento.clientY - pressao.y0
		if (!this.arrastando) {
			if (pressao.longa || Math.hypot(dx, dy) < DISTANCIA_DE_ARRASTO) return
			this.arrastando = true
			window.clearTimeout(this.temporizadorDaPressao)
			this.menuAberto = false
			this.previa = null
		}
		this.posicaoDeArrasto = {
			x: Math.min(this.largura - TAMANHO, Math.max(0, pressao.inicio.x + dx)),
			y: Math.min(this.altura - TAMANHO, Math.max(0, pressao.inicio.y + dy)),
		}
		this.amostras = [...(this.amostras || []).slice(-4), { x: evento.clientX, y: evento.clientY, t: evento.timeStamp }]
	}

	aoSoltar(evento: PointerEvent) {
		const pressao = this.pressao
		if (!pressao || evento.pointerId !== pressao.id) return
		this.pressao = undefined
		window.clearTimeout(this.temporizadorDaPressao)
		if (this.arrastando) this.encaixar()
		else if (!pressao.longa) this.abrir()
	}

	aoCancelar() {
		this.pressao = undefined
		window.clearTimeout(this.temporizadorDaPressao)
		if (this.arrastando) this.encaixar()
	}

	/** Gruda no lado mais próximo (contando o embalo do arrasto) e guarda lado e altura relativa. */
	encaixar() {
		const atual = this.posicaoDeArrasto || this.posicao
		const amostras = this.amostras || []
		const primeira = amostras[0]
		const ultima = amostras[amostras.length - 1]
		const intervalo = primeira && ultima ? Math.max(1, ultima.t - primeira.t) : 1
		const velocidadeX = primeira && ultima ? ((ultima.x - primeira.x) / intervalo) * 1000 : 0
		const velocidadeY = primeira && ultima ? ((ultima.y - primeira.y) / intervalo) * 1000 : 0
		const { topo, base } = this.limites
		const y = Math.min(base, Math.max(topo, atual.y + velocidadeY * 0.05))
		this.lado = atual.x + TAMANHO / 2 + velocidadeX * 0.08 < this.largura / 2 ? 'esquerda' : 'direita'
		this.razaoY = base > topo ? (y - topo) / (base - topo) : 1
		this.posicaoDeArrasto = null
		this.arrastando = false
		try {
			localStorage.setItem(CHAVE_DA_POSICAO, JSON.stringify({ lado: this.lado, y: this.razaoY }))
		} catch {
			// posição é conforto: sem storage, vale só nesta página
		}
	}
}
